import type { ContentGroup, ContentManifest } from '@/features/storefront/content/types';

/**
 * Hyper's own writing, and the reason this whole system exists.
 *
 * This theme is a furniture superstore, and a superstore is made of *words*:
 * four service promises in the footer, a search box with a scope on it, a rail
 * of shelves. Until this manifest existed every one of those sentences was a
 * string literal in a `.tsx` file, shared by every merchant wearing the theme.
 * They are declared here instead, with the words this theme ships as its
 * default, and the components read them through `useContent`.
 *
 * What is *not* here any more is the product page. Its counter and its
 * magazine were markup with sixty sentences in them, and both are data now -
 * a stack of sections and a list of components, in `productPage.ts`. Their
 * words are section content, edited in the inspector beside the band they
 * belong to, which is the right place for a sentence a merchant can also move,
 * duplicate and delete. Declaring them here as well would be two fields
 * disagreeing about one paragraph.
 *
 * Note what is not here either: anything that comes from the shop's own data.
 * A price, a vendor, a variant, a metafield, a neighbouring product - those
 * were never hard-coded and have no business being editable copy. What is here
 * is what a designer wrote because the theme needed a sentence and the database
 * had none.
 */
const groups: readonly ContentGroup[] = [
  // --- Header -------------------------------------------------------------
  {
    id: 'hyper-header-search',
    area: 'header',
    label: 'Search box',
    description: 'The box across the middle deck of the masthead.',
    fields: [
      {
        key: 'header.search.scope',
        label: 'Scope label',
        value: 'All Categories',
        role: 'label',
        hint: 'The words left of the box. A label, not a control.',
        max: 30,
      },
      {
        key: 'header.search.placeholder',
        label: 'Placeholder',
        value: 'What are you looking for?',
        role: 'placeholder',
        max: 60,
      },
      {
        key: 'header.search.label',
        label: 'Field name',
        value: 'Search the shop',
        role: 'label',
        hint: 'Read aloud by screen readers.',
        max: 40,
      },
      { key: 'header.search.submit', label: 'Submit button', value: 'Search', role: 'label', max: 30 },
    ],
  },
  {
    id: 'hyper-header-rail',
    area: 'header',
    label: 'Shelf rail',
    fields: [
      {
        key: 'header.shelves',
        label: 'Rail name',
        value: 'Shelves',
        role: 'label',
        hint: 'The name of the circular department rail, read aloud by screen readers.',
        max: 40,
      },
    ],
  },

  // --- Footer -------------------------------------------------------------
  {
    id: 'hyper-footer-services',
    area: 'footer',
    label: 'Service strip',
    description: 'The four standing promises above the footer proper.',
    fields: [
      {
        key: 'footer.service.support.title',
        label: 'First promise',
        value: 'Customer service',
        role: 'heading',
        max: 40,
      },
      {
        key: 'footer.service.support.body',
        label: 'First promise, detail',
        value: 'We answer our own email, every working day.',
        role: 'message',
        multiline: true,
        max: 160,
      },
      { key: 'footer.service.phone.title', label: 'Second promise', value: 'Call us', role: 'heading', max: 40 },
      {
        key: 'footer.service.phone.body',
        label: 'Second promise, detail',
        value: 'Lines open Monday to Saturday, 10am-6pm IST.',
        role: 'message',
        multiline: true,
        max: 160,
      },
      {
        key: 'footer.service.secure.title',
        label: 'Third promise',
        value: 'Secure checkout',
        role: 'heading',
        max: 40,
      },
      {
        key: 'footer.service.secure.body',
        label: 'Third promise, detail',
        value: 'Pay online or on delivery, whichever you prefer.',
        role: 'message',
        multiline: true,
        max: 160,
      },
      {
        key: 'footer.service.showroom.title',
        label: 'Fourth promise',
        value: 'Visit a showroom',
        role: 'heading',
        max: 40,
      },
      {
        key: 'footer.service.showroom.body',
        label: 'Fourth promise, detail',
        value: 'Sit on it first. Room planning is free.',
        role: 'message',
        multiline: true,
        max: 160,
      },
    ],
  },
  {
    id: 'hyper-footer-columns',
    area: 'footer',
    label: 'Column headings',
    description: 'The headings over the link columns. Which columns appear depends on what you have published.',
    fields: [
      {
        key: 'footer.column.company',
        label: 'Your menu, unnamed',
        value: 'Company',
        role: 'heading',
        hint: 'Used only when your footer menu has no name of its own.',
        max: 40,
      },
      {
        key: 'footer.column.shop',
        label: 'Your categories',
        value: 'Shop',
        role: 'heading',
        max: 40,
      },
      {
        key: 'footer.column.departments',
        label: 'Your categories, alternative',
        value: 'Departments',
        role: 'heading',
        hint: 'Used instead when your own menu is already called "Shop".',
        max: 40,
      },
      {
        key: 'footer.column.help',
        label: 'Pages and blogs',
        value: 'Help',
        role: 'heading',
        max: 40,
      },
    ],
  },
  {
    id: 'hyper-footer-fineprint',
    area: 'footer',
    label: 'The sign-off bar',
    fields: [
      {
        key: 'footer.fine_print.prices',
        label: 'Pricing note',
        value: 'All prices in INR, GST included',
        role: 'legal',
        max: 80,
      },
    ],
  },
];

export const content: ContentManifest = { namespace: 'hyper', groups };
